import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import { Loader2 } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useSpareStockReport } from "@/features/spares/reportsApi";

export const Route = createFileRoute("/_authenticated/reports/spare-stock-register")({
  component: SpareStockRegisterReport,
});

function SpareStockRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [search, setSearch] = useState("");
  const [storeLocation, setStoreLocation] = useState("");
  const [expiryStatus, setExpiryStatus] = useState<"all" | "expired" | "expiring30" | "valid">("all");
  const [lowOnly, setLowOnly] = useState(false);
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data, isLoading } = useSpareStockReport({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId ?? null,
    categoryId: filters.categoryId ?? null,
    storeLocation: storeLocation || null,
    search: search || null,
    expiryStatus,
    lowOnly,
  });
  const rows = data ?? [];

  const exportRows = useMemo(() => () => rows.map((r) => ({
    "Spare Code": r.spare?.code ?? "",
    "Spare Name": r.spare?.name ?? "",
    Branch: r.branch?.name ?? "",
    Department: r.department?.name ?? "",
    "Store Location": r.store_location,
    "Rack / Bin": r.rack_location ?? "",
    "Batch No": r.batch_no ?? "",
    "Expiry Date": r.expiry_date ?? "",
    "Warranty End": r.warranty_end_date ?? "",
    "Qty On Hand": r.qty_on_hand,
    "Reserved": r.qty_reserved,
    "Issued": r.qty_issued,
    "Available": r.available,
    "Reorder Level": r.spare?.min_qty ?? 0,
    Status: r.spare?.active ? "Active" : "Inactive",
  })), [rows]);

  return (
    <ReportShell
      id="R-35" title="Spare Stock Register"
      description="Current spare stock by branch / store / department with batch and expiry."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Store Location</Label>
            <Input value={storeLocation} onChange={(e) => setStoreLocation(e.target.value)} placeholder="Any store" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expiry</Label>
            <Select value={expiryStatus} onValueChange={(v) => setExpiryStatus(v as typeof expiryStatus)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="expiring30">Expiring in 30 days</SelectItem>
                <SelectItem value="valid">Valid / No expiry</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code / name / batch" />
          </div>
          <div className="flex items-end gap-2">
            <Switch id="low" checked={lowOnly} onCheckedChange={setLowOnly} />
            <Label htmlFor="low" className="text-xs">Low stock only</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Dept</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Rack</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead className="text-right">On Hand</TableHead>
                <TableHead className="text-right">Reserved</TableHead>
                <TableHead className="text-right">Issued</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">Reorder</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const low = r.available <= (r.spare?.min_qty ?? 0);
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.spare?.code}</TableCell>
                      <TableCell className="font-medium">{r.spare?.name}</TableCell>
                      <TableCell>{r.branch?.name ?? "—"}</TableCell>
                      <TableCell>{r.department?.name ?? "—"}</TableCell>
                      <TableCell>{r.store_location}</TableCell>
                      <TableCell>{r.rack_location ?? "—"}</TableCell>
                      <TableCell>{r.batch_no ?? "—"}</TableCell>
                      <TableCell>{r.expiry_date ?? "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.qty_on_hand}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.qty_reserved}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.qty_issued}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.available}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.spare?.min_qty ?? 0}</TableCell>
                      <TableCell>
                        {low ? <Badge variant="destructive">Low</Badge> : <Badge variant="secondary">OK</Badge>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={14} className="py-8 text-center text-sm text-muted-foreground">No spare records found for the selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} stock rows.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
